import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Home, 
  BarChart3, 
  TrendingUp, 
  Search, 
  User, 
  LogOut,
  Settings,
  Bell,
  User as UserIcon,
  ChevronDown,
  Sparkles,
  Clock,
  Calendar,
  MessageCircle,
  BookOpen,
  GraduationCap,
  FileText
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/ThemeContext';
import { tradingAPI } from '../../services/api';

// Helper to get London time (avoid client timezone drift)
const getLondonTime = () => {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  // Determine if current date is within British Summer Time (BST)
  const startBST = new Date(now.getFullYear(), 2, 31);
  startBST.setDate(31 - (startBST.getDay() || 7) + 7);
  const endBST = new Date(now.getFullYear(), 9, 31);
  endBST.setDate(31 - (endBST.getDay() || 7));
  const offset = (now >= startBST && now < endBST) ? 1 : 0;
  return new Date(utc + 3600000 * offset);
};

// Prefer server-provided London time to avoid client timezone drift
const parseServerHMS = (hms) => {
  if (!hms || typeof hms !== 'string') return { hour: 0, minute: 0 };
  const [hh, mm] = hms.split(':');
  const hour = parseInt(hh || '0', 10);
  const minute = parseInt(mm || '0', 10);
  return { hour: isNaN(hour) ? 0 : hour, minute: isNaN(minute) ? 0 : minute };
};

// Helper to parse HH:MM into [h, m]
const parseHM = (hm) => {
  if (!hm || typeof hm !== 'string') return [16, 30];
  const [h, m] = hm.split(':').map((x) => parseInt(x, 10));
  return [isNaN(h) ? 16 : h, isNaN(m) ? 30 : m];
};

// Compute next open and close times based on today and market hours
const getNextOpenMinutes = (day, hour, minute) => {
  // day: 0 Sun .. 6 Sat (server provides day_of_week name; we'll map below)
  const now = hour * 60 + minute;
  const openMin = 8 * 60;
  if (day === 0 || day === 6) {
    // Weekend: to next Monday 08:00
    const days = (1 - day + 7) % 7 || 1;
    return (24 * 60 - now) + (days - 1) * 1440 + openMin;
  }
  if (now <= openMin) return openMin - now; // before or at 08:00 today
  // After 08:00: go to next business day 08:00
  let days = 1;
  let dow = (day + days) % 7;
  while (dow === 0 || dow === 6) { days += 1; dow = (day + days) % 7; }
  return (24 * 60 - now) + (days - 1) * 1440 + openMin;
};

const getTodayCloseMinutes = (hour, minute, closeHM = '16:30') => {
  const [ch, cm] = parseHM(closeHM);
  const now = hour * 60 + minute;
  const closeMin = ch * 60 + cm;
  return Math.max(0, closeMin - now);
};

// Helper to format time difference
const formatMinutes = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatTime = (date) => {
  if (!date) return '--:--';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [ukMarketOpen, setUkMarketOpen] = useState(false);
  const [marketStatusText, setMarketStatusText] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [serverHours, setServerHours] = useState('08:00 - 16:30');

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home, description: 'Overview & analytics' },
    { name: 'Portfolio', href: '/portfolio', icon: BarChart3, description: 'Holdings & performance' },
    { name: 'Order Book', href: '/trending', icon: TrendingUp, description: 'Trading activity' },
    { name: 'Research', href: '/research', icon: Search, description: 'Market analysis' },
    { name: 'Tutorial', href: '/tutorial', icon: BookOpen, description: 'Learn trading skills & earn certificates' },
    { name: 'Leaderboard', href: '/leaderboard', icon: GraduationCap, description: 'Monthly rankings' },
    { name: 'Journal', href: '/journal', icon: FileText, description: 'Trading journal & notes' },
    { name: 'Analytics', href: '/analytics', icon: BarChart3, description: 'Performance insights' },
    { name: 'Profile', href: '/profile', icon: User, description: 'Account settings' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => location.pathname === href;

  useEffect(() => {
    const updateMarketStatus = async () => {
      try {
        const res = await tradingAPI.getPublicMarketStatus();
        const data = res?.data || {};
        const isOpen = !!data.market_open;
        setUkMarketOpen(isOpen);
        if (typeof data.market_hours === 'string') setServerHours(data.market_hours);

        // Parse close time from server (e.g., "08:00 - 16:30")
        const parts = (data.market_hours || '08:00 - 16:30').split('-').map((s) => s.trim());
        const openHM = parts[0] || '08:00';
        const closeHM = parts[1] || '16:30';
        const { hour: sh, minute: sm } = parseServerHMS(data.london_time);
        const dayMap = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
        const sd = dayMap[data.day_of_week] ?? new Date().getDay();

        if (isOpen) {
          const minutesLeft = getTodayCloseMinutes(sh, sm, closeHM);
          setMarketStatusText(`Closes ${closeHM} (${formatMinutes(minutesLeft)})`);
        } else {
          const minutesUntil = getNextOpenMinutes(sd, sh, sm);
          setMarketStatusText(`Opens ${openHM} (${formatMinutes(minutesUntil)})`);
        }
      } catch (e) {
        // Fallback to client-only logic if server call fails
        const londonNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }));
        const hour = londonNow.getHours();
        const minute = londonNow.getMinutes();
        const day = londonNow.getDay();
        const isOpen = !(day === 0 || day === 6) && hour >= 8 && (hour < 16 || (hour === 16 && minute < 30));
        setUkMarketOpen(isOpen);
        if (isOpen) {
          const minutesLeft = getTodayCloseMinutes(hour, minute);
          setMarketStatusText(`Closes 16:30 (${formatMinutes(minutesLeft)})`);
        } else {
          const minutesUntil = getNextOpenMinutes(day, hour, minute);
          setMarketStatusText(`Opens 08:00 (${formatMinutes(minutesUntil)})`);
        }
      }
    };

    updateMarketStatus();
    const interval = setInterval(updateMarketStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Update current time every second using London time
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(getLondonTime());
    }, 1000);
    return () => clearInterval(timeInterval);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setSettingsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClearCache = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-white dark:bg-gray-800 shadow-2xl">
          <div className="flex h-16 items-center justify-between px-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">ET</span>
              </div>
              <span className="ml-2 text-lg font-semibold text-gray-900 dark:text-white">EducateTrade</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-white transition-colors duration-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <nav className="flex-1 space-y-2 px-4 py-6">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.name}
                  href={item.href}
                  className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'bg-primary-50 text-primary-900 dark:bg-primary-900/30 dark:text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  <div className="flex-1">
                    <div>{item.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{item.description}</div>
                  </div>
                </a>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex h-16 items-center px-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">ET</span>
              </div>
              <span className="ml-2 text-lg font-semibold text-gray-900 dark:text-white">EducateTrade</span>
            </div>
          </div>
          
          <nav className="flex-1 space-y-2 px-4 py-6">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.name}
                  href={item.href}
                  className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'bg-primary-50 text-primary-900 dark:bg-primary-900/30 dark:text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                  }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  <div className="flex-1">
                    <div>{item.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{item.description}</div>
                  </div>
                </a>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Enhanced Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 dark:text-gray-200 lg:hidden hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Current Time */}
              <div className="hidden sm:flex items-center space-x-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {currentTime.toLocaleTimeString('en-GB', { 
                    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false 
                  })}
                </span>
              </div>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
              >
                {isDark ? (
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                )}
              </button>

              {/* Enhanced Market Status */}
              <div className="relative flex items-center space-x-3">
                <div className={`relative p-2 rounded-full transition-all duration-300 ${
                  ukMarketOpen 
                    ? 'bg-green-100 dark:bg-green-900/30 ring-2 ring-green-200 dark:ring-green-800' 
                    : 'bg-red-100 dark:bg-red-900/30 ring-2 ring-red-200 dark:ring-red-800'
                }`}>
                  <Bell className={`h-5 w-5 ${ukMarketOpen ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} />
                </div>
                
                <div className="hidden sm:block">
                  <div className="text-sm font-medium">
                    {ukMarketOpen ? (
                      <span className="text-green-700 dark:text-green-300">Market Open</span>
                    ) : (
                      <span className="text-red-700 dark:text-red-300">Market Closed</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {marketStatusText}
                  </div>
                </div>
              </div>

              {/* Enhanced Profile Dropdown */}
              <div className="relative" ref={profileRef}>
                <button
                  className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none transition-colors duration-200 group"
                  onClick={() => setProfileOpen((open) => !open)}
                  title="Profile"
                >
                  {/* Avatar Circle */}
                  {user && user.profile_image ? (
                    <img
                      src={user.profile_image}
                      alt="avatar"
                      className="w-8 h-8 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600 group-hover:border-primary-300 dark:group-hover:border-primary-600 transition-colors duration-200"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold text-sm border-2 border-gray-200 dark:border-gray-600 group-hover:border-primary-300 dark:group-hover:border-primary-600 transition-colors duration-200">
                      {user && user.first_name ? user.first_name.charAt(0).toUpperCase() : <UserIcon className="w-5 h-5" />}
                    </div>
                  )}
                  <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-200" />
                </button>
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 backdrop-blur-sm">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center space-x-3">
                        {user && user.profile_image ? (
                          <img
                            src={user.profile_image}
                            alt="avatar"
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold">
                            {user && user.first_name ? user.first_name.charAt(0).toUpperCase() : <UserIcon className="w-5 h-5" />}
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user?.first_name} {user?.last_name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {user?.email}
                          </div>
                        </div>
                      </div>
                    </div>
                    <ul className="py-2">
                      <li>
                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors duration-200"
                          onClick={() => { navigate('/profile'); setProfileOpen(false); }}
                        >
                          View Profile
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Enhanced Settings Dropdown */}
              <div className="relative" ref={settingsRef}>
                <button
                  className="p-2 text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                  onClick={() => setSettingsOpen((open) => !open)}
                  title="Settings"
                >
                  <Settings className="h-5 w-5" />
                </button>
                {settingsOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 backdrop-blur-sm">
                    <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">Settings</h3>
                    </div>
                    <ul className="py-2">
                      <li>
                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors duration-200"
                          onClick={() => { toggleTheme(); setSettingsOpen(false); }}
                        >
                          Change Theme
                        </button>
                      </li>
                      <li>
                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors duration-200"
                          onClick={() => { navigate('/refer'); setSettingsOpen(false); }}
                        >
                          Refer and Earn
                        </button>
                      </li>
                      <li>
                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors duration-200"
                          onClick={() => { handleClearCache(); setSettingsOpen(false); }}
                        >
                          Clear Cache
                        </button>
                      </li>
                      <li>
                        <button
                          className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors duration-200"
                          onClick={() => { navigate('/security'); setSettingsOpen(false); }}
                        >
                          Security
                        </button>
                      </li>
                      <li className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                        <button
                          className="w-full text-left px-4 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 transition-colors duration-200"
                          onClick={() => { handleLogout(); setSettingsOpen(false); }}
                        >
                          Logout
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Logout icon button */}
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-red-500 dark:text-gray-300 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 
